/**
 * Cuadrillas — estado de los equipos de terreno.
 *
 * Field_Ops (R8): además del estado de los equipos, muestra la
 * optimización determinista de rutas (R8.4) calculada por el servicio
 * `fieldOps` a través del runtime selector (R17.2).
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { Icon } from "../components/Icon";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { crewRoute, seedFieldOps, type CuadrillaDemo } from "../lib/fieldOpsRuntime";

interface Cuadrilla {
  cuadrilla_id: string;
  nombre: string;
  tipo: string;
  estado: "disponible" | "en_ruta" | "en_sitio" | "fuera_turno";
  miembros: number;
  ordenes_activas: number;
  ordenes_cerradas_hoy: number;
  eficiencia_pct: number;
  zona: string;
}

function estadoTone(e: string): "accent" | "info" | "warn" | "neutral" {
  if (e === "disponible") return "accent";
  if (e === "en_ruta") return "info";
  if (e === "en_sitio") return "warn";
  return "neutral";
}

/**
 * Panel de optimización de rutas Field_Ops (R8.4): siembra Cuadrillas y
 * Ordenes desde los datos reales y muestra la secuencia de visitas y el ETA
 * por orden, calculados de forma determinista por el servicio `fieldOps`
 * vía el runtime selector.
 */
function RouteOptimizationPanel(): JSX.Element {
  const seed = useQuery<CuadrillaDemo[]>({
    queryKey: ["fieldops.seed"],
    queryFn: () => seedFieldOps(),
    staleTime: 60_000,
  });

  const cuadrillas = seed.data ?? [];

  const rutas = useQuery({
    queryKey: ["fieldops.rutas", cuadrillas.map((c) => c.cuadrilla_id).join(",")],
    enabled: cuadrillas.length > 0,
    queryFn: async () => {
      const out: Record<string, Awaited<ReturnType<typeof crewRoute>>> = {};
      for (const c of cuadrillas) {
        out[c.cuadrilla_id] = await crewRoute(
          c.cuadrilla_id,
          c.ordenes.map((o) => o.orden_ref),
        );
      }
      return out;
    },
  });

  if (cuadrillas.length === 0) return <></>;

  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <h2 className="card__title">
        <span>Optimización de rutas · Field_Ops</span>
        <span className="badge badge--info">R8.4 · determinista</span>
      </h2>
      <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
        Secuencia de visitas (nearest-neighbor) y ETA por orden, recalculados al cambiar ubicación u órdenes (≤10s).
      </p>
      <div className="tile-grid">
        {cuadrillas.map((c) => {
          const ruta = rutas.data?.[c.cuadrilla_id] ?? null;
          return (
            <div key={c.cuadrilla_id} className="card">
              <div className="card__title">
                <span>{c.cuadrilla_id}</span>
                <span className="badge badge--neutral">{c.dma_ref.id}</span>
              </div>
              <div className="text-muted" style={{ fontSize: 11, marginBottom: 6 }}>
                {c.ordenes.length} órdenes asignadas
              </div>
              {ruta ? (
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                  {ruta.ruta.map((ref, i) => (
                    <li key={ref.id} style={{ marginBottom: 4 }}>
                      <span className="text-mono">{ref.id}</span>
                      <span className="text-faint" style={{ marginLeft: 6 }}>
                        ETA {ruta.eta[i]?.eta.slice(11, 16) ?? "—"}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-faint" style={{ fontSize: 11 }}>
                  {rutas.isLoading ? "Calculando ruta…" : "Ruta no disponible"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function Crews(): JSX.Element {
  const { toast } = useToast();
  const q = useQuery<Cuadrilla[]>({
    queryKey: ["cuadrillas"],
    queryFn: () => api<Cuadrilla[]>("/cuadrillas"),
    staleTime: 30_000,
  });
  const cuadrillas = q.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Operación"
        title="Cuadrillas"
        subtitle="Disponibilidad, carga y eficiencia de los equipos en terreno."
        actions={
          <button
            className="btn btn--primary"
            onClick={() => toast("Alta de cuadrilla no disponible en modo demo", "info")}
          >
            <Icon name="plus" size={13} /> Nueva cuadrilla
          </button>
        }
      />
      <RouteOptimizationPanel />
      <div className="tile-grid">
        {cuadrillas.map((c) => (
          <div key={c.cuadrilla_id} className="card">
            <div className="card__title">
              <span>{c.nombre}</span>
              <span className={`badge badge--${estadoTone(c.estado)}`}>
                {c.estado.replace("_", " ")}
              </span>
            </div>
            <div className="hstack" style={{ gap: 18, marginBottom: 10 }}>
              <div>
                <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Activas</div>
                <div className="kpi__value" style={{ fontSize: 22 }}>{c.ordenes_activas}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Cerradas hoy</div>
                <div className="kpi__value" style={{ fontSize: 22 }}>{c.ordenes_cerradas_hoy}</div>
              </div>
              <div>
                <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>Eficiencia</div>
                <div className="kpi__value" style={{ fontSize: 22 }}>{c.eficiencia_pct}%</div>
              </div>
            </div>
            <div className="alert-component">
              <span>Tipo</span><strong style={{ textTransform: "capitalize" }}>{c.tipo}</strong>
            </div>
            <div className="alert-component">
              <span>Zona</span><strong>{c.zona}</strong>
            </div>
            <div className="alert-component">
              <span>Miembros</span><strong>{c.miembros}</strong>
            </div>
            <div className="sla-bar" style={{ marginTop: 8 }}>
              <div
                className="sla-bar__fill sla-bar__fill--ok"
                style={{ width: `${c.eficiencia_pct}%`, background: "var(--c-primary-300)" }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
